import { DatabaseIcon } from 'lucide-react';
import { useMatch, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';

import { Button } from '@monksflow/monks-ui';
import {
  PageHeader,
  PageHeaderActions,
  PageHeaderBreadcrumb,
  PageHeaderBreadcrumbLink,
  PageHeaderBreadcrumbPage,
  PageHeaderIdentity,
  PageHeaderToolGroup,
} from '@/shared/ui/layout';
import { StaggerItem } from '@/shared/ui/animations';
import { ROUTES } from '@/shared/config/routes';
import type { Analysis } from '@/entities/analysis';

import { useProjectActionsWorkspaceId } from '../utils/useProjectActionsWorkspaceId';
import { useSectionTitle } from '../utils/useSectionTitle';
import { AnalysisAddToProjectButton } from './AnalysisAddToProjectButton';
import { AnalysisDownloadButton } from './AnalysisDownloadButton';
import { AnalysisStartBriefButton } from './AnalysisStartBriefButton';

/**
 * Stagger slots this header occupies, so content below it can cascade after
 * rather than replaying the same slots. Keep in step with the `StaggerItem`
 * indices below.
 */
export const ANALYSIS_PAGE_HEADER_STAGGER_SLOTS = 1;

interface AnalysisPageHeaderProps {
  analysis: Analysis;
  /** Absent where the workspace has activated no data source, which hides the action. */
  onOpenDataSources?: () => void;
}

/**
 * Page header for the IQ report: the shared `PageHeader` shell with the IQ tool
 * badge, and the report named as the trailing crumb behind it. Both the badge
 * and the leading crumb return to the IQ list, which is the trail's own
 * affordance rather than a second copy of the badge's.
 *
 * Carries the report actions, which the redesign lifts out of the per-section
 * headers so they sit once at the top rather than repeating down the page.
 * Download still exports the section being viewed, read from the route.
 */
export const AnalysisPageHeader = ({ analysis, onOpenDataSources }: AnalysisPageHeaderProps) => {
  const { t } = useTranslation('analysis');
  const navigate = useNavigate();
  const match = useMatch('/iq/:analysisId/:section');

  const workspaceId = useProjectActionsWorkspaceId();

  const section = match?.params.section;
  const sectionTitle = useSectionTitle(section);
  const showDownload = section !== undefined && sectionTitle !== undefined;

  const iq = t('analysisResult.analysisResult.iq');
  const dataSourcesLabel = t('dataSourcesPanel.open');

  return (
    <StaggerItem index={0}>
      <PageHeader className="dark:bg-background">
        <PageHeaderIdentity>
          <PageHeaderToolGroup
            toolId="iq"
            tool={iq}
            category="plan"
            onBadgeClick={() => void navigate(ROUTES.IQ)}
          >
            <PageHeaderBreadcrumb>
              <PageHeaderBreadcrumbLink href={ROUTES.IQ}>{iq}</PageHeaderBreadcrumbLink>
              <PageHeaderBreadcrumbPage>{analysis.brandName}</PageHeaderBreadcrumbPage>
            </PageHeaderBreadcrumb>
          </PageHeaderToolGroup>
        </PageHeaderIdentity>
        <PageHeaderActions>
          {workspaceId && (
            <>
              <AnalysisStartBriefButton
                analysisId={analysis.id}
                workspaceId={workspaceId}
                brand={analysis.brandName}
              />
              <AnalysisAddToProjectButton analysisId={analysis.id} workspaceId={workspaceId} />
            </>
          )}
          {onOpenDataSources && (
            <Button variant="outline" onClick={onOpenDataSources}>
              <DatabaseIcon className="size-4" />
              {dataSourcesLabel}
            </Button>
          )}
          {showDownload && (
            <AnalysisDownloadButton
              analysisId={analysis.id}
              section={section}
              sectionTitle={sectionTitle}
              brand={analysis.brandName}
            />
          )}
        </PageHeaderActions>
      </PageHeader>
    </StaggerItem>
  );
};
